import { useAppSettings } from "../../appSettings";
import { useTheme } from "../../hooks/useTheme";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";

import { SettingsPageHeader, SettingsPanel, SettingsSection } from "./SettingsChrome";

const THEME_OPTIONS = [
  {
    value: "system",
    label: "System",
    description: "Match your OS appearance setting.",
  },
  {
    value: "light",
    label: "Light",
    description: "Always use the light theme.",
  },
  {
    value: "dark",
    label: "Dark",
    description: "Always use the dark theme.",
  },
] as const;

const TIMESTAMP_FORMAT_LABELS = {
  locale: "System default",
  "12-hour": "12-hour",
  "24-hour": "24-hour",
} as const;

export function AppearanceSettingsPanel() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { settings, defaults, updateSettings } = useAppSettings();

  return (
    <SettingsPanel>
      <SettingsPageHeader
        title="Appearance"
        description="Choose how Operators looks across the app on this device."
      />

      <SettingsSection
        title="Text wrapping"
        description="Wrap long lines in code blocks, commands, and text output across all threads. Saved on this device."
      >
        <label className="flex items-center justify-between gap-4 text-sm">
          Word wrap
          <Switch
            checked={settings.wordWrap}
            onCheckedChange={(wordWrap) => updateSettings({ wordWrap })}
          />
        </label>
      </SettingsSection>

      <SettingsSection
        title="Theme & timestamps"
        description="Theme preference applies immediately. Timestamp format affects chat and activity labels."
      >
        <div className="space-y-4">
          <div className="space-y-2" role="radiogroup" aria-label="Theme preference">
            {THEME_OPTIONS.map((option) => {
              const selected = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`flex w-full items-start justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                    selected
                      ? "border-primary/60 bg-primary/8 text-foreground"
                      : "border-border bg-background text-muted-foreground hover:bg-accent"
                  }`}
                  onClick={() => setTheme(option.value)}
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium">{option.label}</span>
                    <span className="text-xs">{option.description}</span>
                  </span>
                  {selected ? (
                    <span className="rounded bg-primary/14 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-primary uppercase">
                      Selected
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground">
            Active theme: <span className="font-medium text-foreground">{resolvedTheme}</span>
          </p>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Timestamp format</p>
              <p className="text-xs text-muted-foreground">
                System default follows your browser or OS time format. <code>12-hour</code> and{" "}
                <code>24-hour</code> force the hour cycle.
              </p>
            </div>
            <Select
              value={settings.timestampFormat}
              onValueChange={(value) => {
                if (value !== "locale" && value !== "12-hour" && value !== "24-hour") return;
                updateSettings({
                  timestampFormat: value,
                });
              }}
            >
              <SelectTrigger className="w-40 shrink-0" aria-label="Timestamp format">
                <SelectValue>{TIMESTAMP_FORMAT_LABELS[settings.timestampFormat]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end">
                <SelectItem value="locale">{TIMESTAMP_FORMAT_LABELS.locale}</SelectItem>
                <SelectItem value="12-hour">{TIMESTAMP_FORMAT_LABELS["12-hour"]}</SelectItem>
                <SelectItem value="24-hour">{TIMESTAMP_FORMAT_LABELS["24-hour"]}</SelectItem>
              </SelectPopup>
            </Select>
          </div>

          {settings.timestampFormat !== defaults.timestampFormat ? (
            <div className="flex justify-end">
              <Button
                size="xs"
                variant="outline"
                onClick={() =>
                  updateSettings({
                    timestampFormat: defaults.timestampFormat,
                  })
                }
              >
                Restore default
              </Button>
            </div>
          ) : null}
        </div>
      </SettingsSection>
    </SettingsPanel>
  );
}
